import { Star } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';

interface Review {
  name: string;
  rating: number;
  text: string;
  date: string;
}

const reviews: Review[] = [
  {
    name: 'Immacolata S.',
    rating: 5,
    text: 'Pizza ottima, anche quella senza glutine; personale qualificato e gentile. Ci andiamo fedelmente da anni.',
    date: '3 mesi fa',
  },
  {
    name: 'Stefano R.',
    rating: 5,
    text: "Pizza che non delude mai, pasta molto digeribile. Le pizze stagionali potrebbero diventare una dipendenza.. L'autunno veramente top, bravi!",
    date: '3 mesi fa',
  },
  {
    name: 'Patrizia R.',
    rating: 5,
    text: 'Pizzeria di qualità, non mi delude mai. Ottimi prodotti e gentilissimi coloro che ci lavorano. Braviiii!!!',
    date: '5 mesi fa',
  },
  {
    name: 'Tiziana M.',
    rating: 5,
    text: 'È da un anno che io e mia figlia abbiamo scoperto di essere celiache ed è una delle pizze senza glutine più buone che abbia mangiato fino ad ora. Complimenti.',
    date: '1 anno fa',
  },
  {
    name: 'Luigi S.',
    rating: 5,
    text: 'Pizza senza glutine senza paragoni, digeribilissima con farcitura di prodotti freschi e di prima qualità. Ambiente pulito e titolari molto cordiali. Una garanzia!',
    date: '2 anni fa',
  },
  {
    name: 'Barbara B.',
    rating: 5,
    text: 'La pizza è spettacolare, buonissima e i titolari sempre gentili, cortesi e veramente simpatici. Per me che amo follemente la pizza, la loro è il TOP!',
    date: '2 anni fa',
  },
  {
    name: 'Carmelo F.',
    rating: 4,
    text: "Ottima pizzeria prevalentemente d'asporto. Ottima pizza, prodotti senza glutine adatti ai celiaci e non, prezzi nella norma. Consigliato.",
    date: '1 anno fa',
  },
  {
    name: 'Alessandra C.',
    rating: 5,
    text: 'Pizza molto buona e ben condita, personale molto simpatico. Super consigliata!',
    date: '7 mesi fa',
  },
];

const GOOGLE_MAPS_URL = 'https://www.google.com/maps/place/?q=place_id:ChIJXcRv8U8EiEcRhY_VEAuVeqo';

const Stars = ({ rating }: { rating: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }, (_, i) => {
      const fill = Math.max(0, Math.min(1, rating - i));
      return (
        <div key={i} className="relative h-4 w-4">
          <Star className="absolute inset-0 h-4 w-4 text-muted-foreground/30" />
          {fill > 0 && (
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${fill * 100}%` }}
            >
              <Star className="h-4 w-4 fill-accent text-accent" />
            </div>
          )}
        </div>
      );
    })}
  </div>
);

const GoogleIcon = () => (
  <svg className="h-5 w-5 shrink-0 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const ReviewsSection = () => {
  return (
    <section id="recensioni" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Dicono di Noi
          </h2>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Stars rating={4.6} />
            <span className="text-foreground font-semibold text-lg">4.6</span>
            <span className="text-muted-foreground text-sm">/ 5</span>
          </div>
          <a
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors text-sm underline underline-offset-4"
          >
            <GoogleIcon />
            Vedi tutte le 315 recensioni su Google
          </a>
        </div>

        <div className="max-w-5xl mx-auto px-8">
          <Carousel
            opts={{ align: 'start', loop: true }}
            plugins={[Autoplay({ delay: 4000, stopOnInteraction: true })]}
          >
            <CarouselContent>
              {reviews.map((review, index) => (
                <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                  <div className="card-elevated p-6 h-full flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-foreground">{review.name}</span>
                      <GoogleIcon />
                    </div>
                    <Stars rating={review.rating} />
                    <p className="text-muted-foreground text-sm leading-relaxed flex-1">"{review.text}"</p>
                    <span className="text-muted-foreground/60 text-xs">{review.date}</span>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
